import type { TattooEntry } from '../types/dashboard';

export function athensTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-GB', {
    timeZone: 'Europe/Athens',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export function toDateInputValue(date: Date): string {
  const f = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Athens', year: 'numeric', month: '2-digit', day: '2-digit' });
  return f.format(date); // returns YYYY-MM-DD in en-CA locale
}

/** Calendar year/month in Europe/Athens (from local "today" when called with new Date()). */
export function getAthensYearMonthParts(date: Date = new Date()): { year: number; month: number } {
  const s = toDateInputValue(date);
  return { year: Number(s.slice(0, 4)), month: Number(s.slice(5, 7)) };
}

/** The month that follows the given calendar month in Athens (e.g. Dec → next Jan). */
export function getNextAthensYearMonth(date: Date = new Date()): { year: number; month: number } {
  const { year, month } = getAthensYearMonthParts(date);
  if (month === 12) return { year: year + 1, month: 1 };
  return { year, month: month + 1 };
}

export function toTimeInputValue(date: Date): string {
  const f = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Athens', hour: '2-digit', minute: '2-digit', hour12: false });
  return f.format(date); // returns HH:MM
}

function getTimeZoneOffset(date: Date, timeZone: string): number {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = formatter.formatToParts(date);
  const value = (type: string) => Number(parts.find((part) => part.type === type)?.value || 0);
  const asUtc = Date.UTC(
    value('year'),
    value('month') - 1,
    value('day'),
    value('hour'),
    value('minute'),
    value('second'),
  );
  return asUtc - date.getTime();
}

export function combineDateTimeAthens(dateStr: string, timeStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hour, minute] = timeStr.split(':').map(Number);
  const baseUtc = new Date(Date.UTC(year, (month || 1) - 1, day || 1, hour || 0, minute || 0, 0, 0));
  const offset = getTimeZoneOffset(baseUtc, 'Europe/Athens');
  const corrected = new Date(baseUtc.getTime() - offset);
  return corrected.toISOString();
}

export function getCycleRange(anchorDate: string | null | undefined, refDate: Date): { start: string; end: string } {
  const anchor = anchorDate ? new Date(anchorDate) : new Date(refDate.getFullYear(), refDate.getMonth(), refDate.getDate());
  const startOfAnchor = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
  const daysSinceAnchor = Math.floor((stripTime(refDate).getTime() - startOfAnchor.getTime()) / 86400000);
  const cycles = Math.max(0, Math.floor(daysSinceAnchor / 15));
  const cycleStart = new Date(startOfAnchor.getTime() + cycles * 15 * 86400000);
  const cycleEnd = new Date(cycleStart.getTime() + 14 * 86400000);
  return { start: toDateInputValue(cycleStart), end: toDateInputValue(cycleEnd) };
}

/** Returns today's date in Europe/Athens timezone (YYYY-MM-DD). Used for daily auto-pay. */
export function getTodayAthens(refDate: Date = new Date()): string {
  const f = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Athens', year: 'numeric', month: '2-digit', day: '2-digit' });
  return f.format(refDate);
}

/** Daily cycle: start and end are the same date (today in Athens). For auto-pay at 23:59. */
export function getDailyCycleRange(refDate: Date = new Date()): { start: string; end: string } {
  const date = getTodayAthens(refDate);
  return { start: date, end: date };
}

export function stripTime(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function sumEntries(entries: TattooEntry[]): number {
  return entries.reduce((total, entry) => total + (Number(entry.cost_eur) || 0), 0);
}

export function getEntryRate(entry: TattooEntry, fallbackRate: number): number {
  if (entry.own_customer) {
    if (entry.work_period === 'external') return 0.75;
    return entry.work_period === 'outside' ? 0.7 : 0.6;
  }
  return fallbackRate;
}

export function sumPayout(entries: TattooEntry[], fallbackRate: number): number {
  return entries.reduce((total, entry) => total + (Number(entry.cost_eur) || 0) * getEntryRate(entry, fallbackRate), 0);
}

export function sumPayoutByArtist(entries: TattooEntry[], rateByArtistId: Record<string, number>): number {
  return entries.reduce((total, entry) => {
    const rate = typeof rateByArtistId[entry.artist_id] === 'number' ? rateByArtistId[entry.artist_id] : 0.25;
    return total + (Number(entry.cost_eur) || 0) * getEntryRate(entry, rate);
  }, 0);
}

export function groupEntriesByDate(entries: TattooEntry[]): Record<string, TattooEntry[]> {
  return entries.reduce<Record<string, TattooEntry[]>>((acc, entry) => {
    const key = entry.performed_date;
    if (!acc[key]) acc[key] = [];
    acc[key].push(entry);
    return acc;
  }, {});
}

export function getBestDay(entries: TattooEntry[]): { date: string | null; total: number } {
  const grouped = groupEntriesByDate(entries);
  let bestDate: string | null = null;
  let bestTotal = 0;
  for (const [date, list] of Object.entries(grouped)) {
    const total = sumEntries(list);
    if (total > bestTotal) {
      bestTotal = total;
      bestDate = date;
    }
  }
  return { date: bestDate, total: bestTotal };
}

export function groupEntriesByMonth(entries: TattooEntry[]): Record<string, TattooEntry[]> {
  return entries.reduce<Record<string, TattooEntry[]>>((acc, entry) => {
    const key = entry.performed_date.slice(0, 7);
    if (!acc[key]) acc[key] = [];
    acc[key].push(entry);
    return acc;
  }, {});
}

export function getBestMonth(entries: TattooEntry[]): { month: string | null; total: number } {
  const grouped = groupEntriesByMonth(entries);
  let bestMonth: string | null = null;
  let bestTotal = 0;
  for (const [month, list] of Object.entries(grouped)) {
    const total = sumEntries(list);
    if (total > bestTotal) {
      bestTotal = total;
      bestMonth = month;
    }
  }
  return { month: bestMonth, total: bestTotal };
}

/** Best day by artist payout (their share), not gross revenue. */
export function getBestDayPayout(entries: TattooEntry[], fallbackRate: number): { date: string | null; total: number } {
  const grouped = groupEntriesByDate(entries);
  let bestDate: string | null = null;
  let bestTotal = 0;
  for (const [date, list] of Object.entries(grouped)) {
    const total = sumPayout(list, fallbackRate);
    if (total > bestTotal) {
      bestTotal = total;
      bestDate = date;
    }
  }
  return { date: bestDate, total: bestTotal };
}

/** Best month by artist payout (their share), not gross revenue. */
export function getBestMonthPayout(entries: TattooEntry[], fallbackRate: number): { month: string | null; total: number } {
  const grouped = groupEntriesByMonth(entries);
  let bestMonth: string | null = null;
  let bestTotal = 0;
  for (const [month, list] of Object.entries(grouped)) {
    const total = sumPayout(list, fallbackRate);
    if (total > bestTotal) {
      bestTotal = total;
      bestMonth = month;
    }
  }
  return { month: bestMonth, total: bestTotal };
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('el-GR', { style: 'currency', currency: 'EUR' }).format(amount);
}

/* ── Chart data helpers ──────────────────────────────────────── */

export type MonthlyAggregate = { month: string; total: number; count: number; avg: number };

export function calculateMovingAverage(data: { amount: number }[], windowSize: number): number[] {
  const result: number[] = [];
  for (let i = 0; i < data.length; i++) {
    const start = Math.max(0, i - windowSize + 1);
    const window = data.slice(start, i + 1);
    const avg = window.reduce((sum, d) => sum + (Number(d.amount) || 0), 0) / window.length;
    result.push(avg);
  }
  return result;
}

export function groupEntriesByMonthRevenue(entries: TattooEntry[]): { month: string; revenue: number; cash: number; card: number }[] {
  const map: Record<string, { revenue: number; cash: number; card: number }> = {};
  for (const e of entries) {
    const key = e.performed_date.slice(0, 7);
    if (!map[key]) map[key] = { revenue: 0, cash: 0, card: 0 };
    map[key].revenue += Number(e.cost_eur) || 0;
    if (e.payment_method === 'cash') {
      map[key].cash += Number(e.cost_eur) || 0;
    } else if (e.payment_method === 'card') {
      map[key].card += Number(e.cost_eur) || 0;
    } else if (e.payment_method === 'split') {
      map[key].cash += Number(e.cash_amount) || 0;
      map[key].card += Number(e.card_amount) || 0;
    }
  }
  return Object.entries(map)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, v]) => ({ month, ...v }));
}

export function groupEntriesByArtistRevenue(
  entries: TattooEntry[],
  artistNames: Record<string, string>,
): { artistId: string; name: string; revenue: number }[] {
  const map: Record<string, number> = {};
  for (const e of entries) {
    map[e.artist_id] = (map[e.artist_id] || 0) + (Number(e.cost_eur) || 0);
  }
  return Object.entries(map)
    .sort(([, a], [, b]) => b - a)
    .map(([artistId, revenue]) => ({ artistId, name: artistNames[artistId] || artistId, revenue }));
}

export function generateCsvString(headers: string[], rows: string[][]): string {
  const escape = (v: string) => {
    if (v.includes(',') || v.includes('"') || v.includes('\n')) {
      return `"${v.replace(/"/g, '""')}"`;
    }
    return v;
  };
  const lines = [headers.map(escape).join(',')];
  for (const row of rows) {
    lines.push(row.map(escape).join(','));
  }
  return lines.join('\n');
}

export function downloadCsv(filename: string, csvContent: string) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/* ── Analytics helpers ──────────────────────────────────── */

export function groupEntriesByHour(entries: TattooEntry[]): { hour: number; count: number; revenue: number }[] {
  const map: Record<number, { count: number; revenue: number }> = {};
  for (let h = 0; h < 24; h++) map[h] = { count: 0, revenue: 0 };
  for (const e of entries) {
    const date = new Date(e.performed_at);
    const athensHour = Number(
      new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Athens', hour: '2-digit', hour12: false }).format(date),
    );
    map[athensHour].count += 1;
    map[athensHour].revenue += Number(e.cost_eur) || 0;
  }
  return Object.entries(map)
    .map(([hour, v]) => ({ hour: Number(hour), ...v }))
    .sort((a, b) => a.hour - b.hour);
}

export function groupEntriesByDayAndHour(entries: TattooEntry[]): { day: number; hour: number; count: number; revenue: number }[] {
  const map: Record<string, { count: number; revenue: number }> = {};
  for (const e of entries) {
    const date = new Date(e.performed_at);
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Athens',
      weekday: 'short',
      hour: '2-digit',
      hour12: false,
    }).formatToParts(date);
    const dayStr = parts.find((p) => p.type === 'weekday')?.value || '';
    const hourStr = parts.find((p) => p.type === 'hour')?.value || '0';
    const dayMap: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
    const day = dayMap[dayStr] ?? 0;
    const hour = Number(hourStr);
    const key = `${day}-${hour}`;
    if (!map[key]) map[key] = { count: 0, revenue: 0 };
    map[key].count += 1;
    map[key].revenue += Number(e.cost_eur) || 0;
  }
  return Object.entries(map).map(([key, v]) => {
    const [d, h] = key.split('-').map(Number);
    return { day: d, hour: h, ...v };
  });
}

export function groupEntriesByCustomerType(entries: TattooEntry[]): { type: string; count: number; revenue: number }[] {
  let ownCount = 0, ownRev = 0, studioCount = 0, studioRev = 0;
  for (const e of entries) {
    const cost = Number(e.cost_eur) || 0;
    if (e.own_customer) { ownCount++; ownRev += cost; }
    else { studioCount++; studioRev += cost; }
  }
  return [
    { type: 'Πελάτες καλλιτέχνη', count: ownCount, revenue: ownRev },
    { type: 'Πελάτες στούντιο', count: studioCount, revenue: studioRev },
  ];
}

export function groupEntriesByWorkPeriod(entries: TattooEntry[]): { period: string; count: number; revenue: number }[] {
  const labels: Record<string, string> = {
    inside: 'Εντός',
    outside: 'Εκτός',
    external: 'Εξωτερικό',
    none: 'Στούντιο (μη ίδιος)',
  };
  const map: Record<string, { count: number; revenue: number }> = {};
  for (const e of entries) {
    const key = e.work_period || 'none';
    if (!map[key]) map[key] = { count: 0, revenue: 0 };
    map[key].count += 1;
    map[key].revenue += Number(e.cost_eur) || 0;
  }
  return Object.entries(map).map(([key, v]) => ({ period: labels[key] || key, ...v }));
}

export function calculateAvgTicketByDate(entries: TattooEntry[]): { date: string; avg: number; count: number }[] {
  const map: Record<string, { total: number; count: number }> = {};
  for (const e of entries) {
    if (!map[e.performed_date]) map[e.performed_date] = { total: 0, count: 0 };
    map[e.performed_date].total += Number(e.cost_eur) || 0;
    map[e.performed_date].count += 1;
  }
  return Object.entries(map)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, v]) => ({ date, avg: v.count > 0 ? v.total / v.count : 0, count: v.count }));
}

export function calculateAvgTicketByMonth(entries: TattooEntry[]): { month: string; avg: number; count: number; total: number }[] {
  const map: Record<string, { total: number; count: number }> = {};
  for (const e of entries) {
    const key = e.performed_date.slice(0, 7);
    if (!map[key]) map[key] = { total: 0, count: 0 };
    map[key].total += Number(e.cost_eur) || 0;
    map[key].count += 1;
  }
  return Object.entries(map)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, v]) => ({ month, avg: v.count > 0 ? v.total / v.count : 0, count: v.count, total: v.total }));
}

export function calculateTipStats(entries: TattooEntry[]): {
  totalTips: number;
  avgTipPerEntry: number;
  monthly: { month: string; tips: number; count: number; avgTip: number }[];
} {
  const map: Record<string, { tips: number; count: number }> = {};
  let totalTips = 0;
  let tippedCount = 0;
  for (const e of entries) {
    const tip = Number(e.tip_eur) || 0;
    totalTips += tip;
    if (tip > 0) tippedCount++;
    const key = e.performed_date.slice(0, 7);
    if (!map[key]) map[key] = { tips: 0, count: 0 };
    map[key].tips += tip;
    if (tip > 0) map[key].count += 1;
  }
  const monthly = Object.entries(map)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, v]) => ({ month, tips: v.tips, count: v.count, avgTip: v.count > 0 ? v.tips / v.count : 0 }));
  return {
    totalTips,
    avgTipPerEntry: tippedCount > 0 ? totalTips / tippedCount : 0,
    monthly,
  };
}

export function calculatePaymentMethodTrend(entries: TattooEntry[]): { month: string; cash: number; card: number; total: number }[] {
  const map: Record<string, { cash: number; card: number }> = {};
  for (const e of entries) {
    const key = e.performed_date.slice(0, 7);
    if (!map[key]) map[key] = { cash: 0, card: 0 };
    if (e.payment_method === 'cash') {
      map[key].cash += Number(e.cost_eur) || 0;
    } else if (e.payment_method === 'card') {
      map[key].card += Number(e.cost_eur) || 0;
    } else if (e.payment_method === 'split') {
      map[key].cash += Number(e.cash_amount) || 0;
      map[key].card += Number(e.card_amount) || 0;
    }
  }
  return Object.entries(map)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, v]) => ({ month, cash: v.cash, card: v.card, total: v.cash + v.card }));
}

export function countEntriesByDate(entries: TattooEntry[]): { date: string; count: number }[] {
  const map: Record<string, number> = {};
  for (const e of entries) {
    map[e.performed_date] = (map[e.performed_date] || 0) + 1;
  }
  return Object.entries(map)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, count]) => ({ date, count }));
}

/* ── Day-of-week analysis ─────────────────────────────────────── */

export function groupEntriesByDayOfWeek(entries: TattooEntry[]): { day: number; dayName: string; revenue: number; count: number; avg: number }[] {
  const dayLabels = ['Δευ', 'Τρι', 'Τετ', 'Πεμ', 'Παρ', 'Σαβ', 'Κυρ'];
  const map: Record<number, { revenue: number; count: number }> = {};
  for (let i = 0; i < 7; i++) map[i] = { revenue: 0, count: 0 };
  for (const e of entries) {
    const jsDay = new Date(e.performed_date).getDay(); // 0=Sun
    const day = jsDay === 0 ? 6 : jsDay - 1; // convert to 0=Mon
    map[day].revenue += Number(e.cost_eur) || 0;
    map[day].count += 1;
  }
  const dateSets: Record<number, Set<string>> = {};
  for (let i = 0; i < 7; i++) dateSets[i] = new Set();
  for (const e of entries) {
    const jsDay = new Date(e.performed_date).getDay();
    const day = jsDay === 0 ? 6 : jsDay - 1;
    dateSets[day].add(e.performed_date);
  }
  return Array.from({ length: 7 }, (_, i) => {
    const uniqueDays = dateSets[i].size || 1;
    return {
      day: i,
      dayName: dayLabels[i],
      revenue: map[i].revenue,
      count: map[i].count,
      avg: map[i].revenue / uniqueDays,
    };
  });
}

export function projectMonthEnd(currentTotal: number, daysElapsed: number, totalDays: number): number {
  if (daysElapsed <= 0) return 0;
  return (currentTotal / daysElapsed) * totalDays;
}

/* ── Net Profit helpers ───────────────────────────────────────── */

export function countWorkingDays(year: number, month: number): number {
  const daysInMonth = new Date(year, month, 0).getDate();
  let count = 0;
  for (let day = 1; day <= daysInMonth; day++) {
    const dow = new Date(year, month - 1, day).getDay(); // 0=Sun, 6=Sat
    if (dow >= 1 && dow <= 5) count++;
  }
  return count;
}

export type NetProfitBreakdown = {
  totalRevenue: number;
  cardRevenue: number;
  artistExpenses: number;
  cardExpenses: number;
  fixedExpenses: number;
  netIncome: number;
};

export function calculateNetProfit(params: {
  totalRevenue: number;
  cardRevenue: number;
  artistFeeRate?: number;
  cardFeeRate?: number;
  fixedExpenses?: number;
}): NetProfitBreakdown {
  const artistFeeRate = params.artistFeeRate ?? 0.25;
  const cardFeeRate = params.cardFeeRate ?? 0.24;
  const fixedExpenses = params.fixedExpenses ?? 4200;

  const artistExpenses = params.totalRevenue * artistFeeRate;
  const cardExpenses = params.cardRevenue * cardFeeRate;
  const netIncome = params.totalRevenue - artistExpenses - cardExpenses - fixedExpenses;

  return {
    totalRevenue: params.totalRevenue,
    cardRevenue: params.cardRevenue,
    artistExpenses,
    cardExpenses,
    fixedExpenses,
    netIncome,
  };
}
